import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";

const DataContext = createContext();

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within DataProvider");
  return ctx;
};

export const DataProvider = ({ children }) => {
  const { token } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [actions, setActions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [camps, acts, lds, cds, us] = await Promise.all([
        api.listCampaigns(token),
        api.listActions(token),
        api.listLeads(token),
        api.listCodes(token),
        api.listUsers(token),
      ]);
      setCampaigns(camps);
      setActions(acts);
      setLeads(lds);
      setCodes(cds);
      setUsers(us);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const createCampaign = async (data) => {
    const c = await api.createCampaign(token, data);
    setCampaigns((p) => [c, ...p]);
    return c;
  };
  const updateCampaign = async (id, data) => {
    const c = await api.updateCampaign(token, id, data);
    setCampaigns((p) => p.map((x) => (x.id === id ? c : x)));
    return c;
  };
  const deleteCampaign = async (id) => {
    await api.deleteCampaign(token, id);
    setCampaigns((p) => p.filter((x) => x.id !== id));
  };
  const createAction = async (data) => {
    const a = await api.createAction(token, data);
    setActions((p) => [a, ...p]);
    return a;
  };
  const updateAction = async (id, data) => {
    const a = await api.updateAction(token, id, data);
    setActions((p) => p.map((x) => (x.id === id ? a : x)));
    return a;
  };
  const deleteAction = async (id) => {
    await api.deleteAction(token, id);
    setActions((p) => p.filter((x) => x.id !== id));
  };
  const refreshLeads = async () => {
    if (!token) return;
    const l = await api.listLeads(token);
    setLeads(l);
  };
  const createUser = async (data) => {
    const u = await api.createUser(token, data);
    setUsers((p) => [...p, u]);
    return u;
  };
  const deleteUser = async (id) => {
    await api.deleteUser(token, id);
    setUsers((p) => p.filter((u) => u.id !== id));
  };

  return (
    <DataContext.Provider
      value={{
        campaigns,
        actions,
        leads,
        codes,
        users,
        loading,
        reload: loadAll,
        createCampaign,
        updateCampaign,
        deleteCampaign,
        createAction,
        updateAction,
        deleteAction,
        refreshLeads,
        createUser,
        deleteUser,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
